import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  type SelectProps,
  FormHelperText,
} from '@mui/material';
import { forwardRef } from 'react';
import { colors, radius, fonts, spacing } from '@/theme/tokens';

interface AppSelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

interface AppSelectProps extends Omit<SelectProps, 'variant'> {
  options: AppSelectOption[];
  helperText?: string;
}

export const AppSelect = forwardRef<HTMLSelectElement, AppSelectProps>(
  ({ options, label, helperText, error, sx, fullWidth, ...props }, ref) => {
    const labelId = `select-label-${label?.toString().toLowerCase().replace(/\s/g, '-')}`;

    return (
      <FormControl fullWidth={fullWidth} error={error} sx={sx}>
        {label && (
          <InputLabel
            id={labelId}
            sx={{
              color: colors.textMuted,
              fontFamily: fonts.body,
              fontSize: '14px',
              '&.Mui-focused': {
                color: colors.accent,
              },
              '&.Mui-error': {
                color: colors.danger,
              },
            }}
          >
            {label}
          </InputLabel>
        )}
        <Select
          ref={ref as React.Ref<HTMLDivElement>}
          labelId={labelId}
          label={label}
          sx={{
            borderRadius: radius.md,
            backgroundColor: colors.surface,
            fontFamily: fonts.body,
            fontSize: '14px',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.border,
              borderWidth: '1px',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.textMuted,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.accent,
              borderWidth: '1px',
            },
            '&.Mui-error .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.danger,
            },
            '& .MuiSelect-select': {
              color: colors.textPrimary,
              padding: `${spacing[3]} ${spacing[4]}`,
            },
            '& .MuiSelect-icon': {
              color: colors.textMuted,
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                backgroundColor: colors.surfaceRaised,
                border: `1px solid ${colors.border}`,
                borderRadius: radius.md,
                marginTop: spacing[1],
                '& .MuiMenuItem-root': {
                  fontFamily: fonts.body,
                  fontSize: '14px',
                  color: colors.textPrimary,
                  padding: `${spacing[3]} ${spacing[4]}`,
                  '&:hover': {
                    backgroundColor: colors.surface,
                  },
                  '&.Mui-selected': {
                    backgroundColor: colors.surface,
                    '&:hover': {
                      backgroundColor: colors.surface,
                    },
                  },
                  '&.Mui-disabled': {
                    color: colors.textMuted,
                    opacity: 0.5,
                  },
                },
              },
            },
          }}
          {...props}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        {helperText && (
          <FormHelperText
            sx={{
              fontFamily: fonts.body,
              fontSize: '12px',
              marginTop: spacing[1],
            }}
          >
            {helperText}
          </FormHelperText>
        )}
      </FormControl>
    );
  }
);

AppSelect.displayName = 'AppSelect';
